import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeTable } from "./useRealtime";
import type { TextMessage } from "@/lib/types";

const TEXT_REALTIME_KEYS = ["text-conversations", "text-conversation", "inbox-stats"];

export type ChatParticipant = { phone: string; name?: string | null };

export type TextConversation = {
  // chat_id is the primary key for groups (e.g. "chat947830126039292...").
  // For 1:1 chats it's the counterparty phone (matches phone_number).
  // Some pre-2026-05-05 rows have chat_id=NULL — we fall back to phone_number
  // in that case so the conversation still groups.
  chat_id: string;
  chat_type: "individual" | "group";
  chat_title: string | null;
  participants: ChatParticipant[] | null;
  phone_number: string;
  contact_name: string | null;
  latest_message: string;
  latest_date: string;
  unread_count: number;
};

type ConversationRow = Pick<
  TextMessage,
  "phone_number" | "contact_name" | "body" | "created_at" | "is_read"
> & {
  chat_id: string | null;
  chat_type: "individual" | "group" | null;
  chat_title: string | null;
  participants: ChatParticipant[] | null;
};

export function useTextConversations() {
  const { user } = useAuth();
  useRealtimeTable("text_messages", TEXT_REALTIME_KEYS);
  return useQuery({
    queryKey: ["text-conversations", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("text_messages")
        .select(
          "phone_number,contact_name,body,created_at,is_read,chat_id,chat_type,chat_title,participants"
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(2000);

      if (error) throw error;

      const convMap = new Map<string, TextConversation>();
      for (const msg of (data || []) as ConversationRow[]) {
        const key = msg.chat_id || msg.phone_number;
        const existing = convMap.get(key);
        if (!existing) {
          convMap.set(key, {
            chat_id: key,
            chat_type: msg.chat_type || "individual",
            chat_title: msg.chat_title,
            participants: msg.participants,
            phone_number: msg.phone_number,
            contact_name: msg.contact_name,
            latest_message: msg.body,
            latest_date: msg.created_at,
            unread_count: !msg.is_read ? 1 : 0,
          });
        } else {
          if (!msg.is_read) existing.unread_count++;
          if (msg.contact_name && !existing.contact_name) {
            existing.contact_name = msg.contact_name;
          }
          if (msg.chat_title && !existing.chat_title) {
            existing.chat_title = msg.chat_title;
          }
          if (msg.participants && !existing.participants) {
            existing.participants = msg.participants;
          }
        }
      }

      return Array.from(convMap.values()).sort(
        (a, b) => new Date(b.latest_date).getTime() - new Date(a.latest_date).getTime()
      );
    },
    enabled: !!user,
  });
}

export function useMarkTextsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (chatId: string) => {
      if (!user) return;
      // chat_id is the canonical conversation key. For pre-group-schema rows
      // where chat_id is NULL, the chatId passed in equals phone_number, so
      // fall back to a phone_number filter when chat_id doesn't match anything.
      const { error: byChatErr } = await supabase
        .from("text_messages")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("chat_id", chatId)
        .eq("is_read", false);
      if (byChatErr) throw byChatErr;

      // Fallback for legacy rows with chat_id=NULL — same caller is using the
      // phone_number as chatId, so try a phone_number-keyed update too.
      const { error: byPhoneErr } = await supabase
        .from("text_messages")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .is("chat_id", null)
        .eq("phone_number", chatId)
        .eq("is_read", false);
      if (byPhoneErr) throw byPhoneErr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["text-conversations"] });
      queryClient.invalidateQueries({ queryKey: ["inbox-stats"] });
    },
  });
}

export function useTextConversation(chatId: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["text-conversation", user?.id, chatId],
    queryFn: async () => {
      if (!user || !chatId) return [];

      // Primary path: query by chat_id. Falls through to a phone_number query
      // when no rows come back (handles legacy chat_id=NULL rows where the
      // caller passed phone_number as chatId).
      const { data: byChat, error: byChatErr } = await supabase
        .from("text_messages")
        .select("*")
        .eq("user_id", user.id)
        .eq("chat_id", chatId)
        .order("created_at", { ascending: false })
        .limit(200);
      if (byChatErr) throw byChatErr;

      let rows = (byChat || []) as TextMessage[];
      if (rows.length === 0) {
        const { data: byPhone, error: byPhoneErr } = await supabase
          .from("text_messages")
          .select("*")
          .eq("user_id", user.id)
          .is("chat_id", null)
          .eq("phone_number", chatId)
          .order("created_at", { ascending: false })
          .limit(200);
        if (byPhoneErr) throw byPhoneErr;
        rows = (byPhone || []) as TextMessage[];
      }

      return rows.reverse();
    },
    enabled: !!user && !!chatId,
  });
}
